import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi, notificationsApi, passkeyApi } from "@/lib/api/endpoints";
import type { NotificationPreferences } from "@/lib/api/types";
import { qk } from "@/lib/query-keys";

export function useSessions() {
  const query = useQuery({ queryKey: qk.sessions, queryFn: authApi.sessions });
  return { ...query, sessions: query.data?.items ?? [] };
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => authApi.revokeSession(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.sessions }),
  });
}

export function usePasskeys() {
  const query = useQuery({ queryKey: qk.passkeys, queryFn: passkeyApi.list });
  return { ...query, passkeys: query.data?.items ?? [] };
}

export function usePasskeyMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.passkeys });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => passkeyApi.rename(id, name),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => passkeyApi.remove(id),
    onSuccess: invalidate,
  });

  return { rename, remove, invalidate };
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(body),
  });
}

export function useResendVerification() {
  return useMutation({ mutationFn: () => authApi.resendVerification() });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: qk.notificationPreferences,
    queryFn: notificationsApi.preferences,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<NotificationPreferences>) =>
      notificationsApi.updatePreferences(body),
    onSuccess: (prefs) => queryClient.setQueryData(qk.notificationPreferences, prefs),
  });
}
